import CheckIcon from "@/assets/icons/check.svg?react";
import {
  TaskCollection,
  TaskList,
  TaskStatus,
  TaskVariant,
} from "@/pages/get-don/tasks.types";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/shared/utils";
import TaskButton from "@/components/base/button/TaskButton";
import EyeIcon from "@/assets/icons/eye.svg?react";
import ChevronRightBold from "@/assets/icons/chevron-right-bold.svg?react";
import { openLink } from "@telegram-apps/sdk-react";
import { openTelegramUsernameLink } from "@/shared/utils/common";
import { useGetProfile } from "@/pages/profile/api";
import DiamondFilledLinear from "@/assets/icons/diamond-filled-linear.svg?react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Fragment, useState } from "react";
import TaskDrawer from "@/pages/get-don/task-actions/TaskDrawer";
import { iconMap } from "./utils";
import Image from "@/components/base/Image.tsx";

type TaskCardProps = TaskList & {
  onAction?: () => void;
  actionComponent?: React.ReactNode;
  task_type: TaskVariant;
};

const TaskCard = (props: TaskCardProps) => {
  const { onAction, actionComponent } = props;
  const [openDrawer, setOpenDrawer] = useState(false);

  const handleOpenDrawer = () => {
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
  };

  const { data: profileData } = useGetProfile();
  const { t } = useTranslation();

  const getImgOrIcon = () => {
    if (props.premium && !profileData?.is_premium) {
      return <DiamondFilledLinear className="size-6" />;
    }

    if (props.user_process?.status === TaskStatus.Completed) {
      return <CheckIcon className="size-6 text-green" />;
    }
    if (props.logo?.url) {
      return (
        <img
          src={props.logo.url ?? ""}
          className="size-6 object-cover"
          alt="logo"
        />
      );
    }
    const Icon = iconMap[props.task_type];
    if (Icon) {
      return <Icon className="size-6 text-white" />;
    }
  };

  const handleGoToLink = () => {
    if (props.description || props.multi_stage) {
      handleOpenDrawer();
      return;
    }

    if (props.task_type === TaskVariant.TelegramChatJoin) {
      openTelegramUsernameLink(props.telegram_chat_id);
      return;
    }

    if (props.link) {
      openLink(props.link);
    }
  };

  const awardPrefix =
    props.task_type === TaskVariant.Invites
      ? `${Math.min(profileData?.referral_count ?? 0, props.target_invite_count)}/${
          props.target_invite_count
        } ${t("friend")}, `
      : props.task_type === TaskVariant.Farm
      ? `${formatNumber(
          Math.min(+(profileData?.wallet_amount ?? 0), +props.target_farm_count)
        )}/${formatNumber(props.target_farm_count)} DON, +`
      : "";

  return (
    <Fragment>
      <div
        className="py-3 flex items-center gap-2 relative cursor-pointer"
        key={props.id}
      >
        <div
          className="bg-white/08 rounded-full w-9 h-9 flex-center shrink-0"
          onClick={handleGoToLink}
        >
          {getImgOrIcon()}
        </div>
        <div className="w-full flex flex-col gap-0.5" onClick={handleGoToLink}>
          <h2 className="text-sm font-semibold leading-130 text-white">
            {props.name}
          </h2>
          <div
            className={cn(
              "text-xs font-normal leading-130 inline-flex items-center gap-0.5",
              props.user_process?.status === TaskStatus.Completed
                ? "text-green"
                : "text-[#FBC76C]"
            )}
          >
            <p className="flex items-center gap-0.5">
              {awardPrefix}
              {formatNumber(props.award)} DON
            </p>
          </div>
        </div>
        {props.coming_soon && (
          <span className="text-gray-1 border-2 border-white/08 text-xs whitespace-nowrap px-2.5 py-[7px] rounded-full font-semibold leading-120">
            {t("soon").replace(/\./g, "")}
          </span>
        )}

        {props.premium && !profileData?.is_premium ? (
          actionComponent
        ) : props.description || props.multi_stage ? (
          <ChevronRightBold
            className="text-white size-6 shrink-0"
            strokeWidth={4}
          />
        ) : (
          actionComponent ?? (
            <TaskButton
              status={props.user_process?.status ?? TaskStatus.Incomplete}
              className="shrink-0"
              onClick={onAction}
            />
          )
        )}
        <hr className="absolute bottom-0 left-[45px] translate-y-[0.5px] border-none h-px w-full bg-white/08 group-last:hidden" />
      </div>
      <TaskDrawer
        task={props}
        onClose={handleCloseDrawer}
        onOpen={handleOpenDrawer}
        isOpen={openDrawer}
        actionComponent={actionComponent}
      />
    </Fragment>
  );
};

export default TaskCard;

const CollectionTaskCard = (props: TaskCollection) => {
  const navigate = useNavigate();
  const getImgOrIcon = () => {
    if (props.logo?.url) {
      return (
        <Image
          imgClassName="size-9 object-cover"
          size="small"
          urls={props.logo}
          alt="logo"
        />
      );
    }

    return <EyeIcon className="size-6 text-white" />;
  };

  return (
    <div
      className="py-3 flex items-center gap-2 relative  cursor-pointer"
      key={props.id}
      onClick={() => {
        navigate(`/get-don/${props.id}`);
      }}
    >
      <div className="bg-white/08 rounded-full size-9 flex-center shrink-0 overflow-hidden">
        {getImgOrIcon()}
      </div>
      <div className="w-full flex flex-col gap-0.5">
        <h2 className="text-sm font-semibold leading-130 text-white">
          {props.name}
        </h2>
        <p
          className={cn(
            "text-xs font-normal leading-130 inline-flex items-center gap-0.5 text-[#FBC76C]"
          )}
        >
          {formatNumber(props.total_award)}{" "}
          <span className="uppercase tracking-wide">don</span>
        </p>
      </div>
      <ChevronRightBold className="text-white size-6" strokeWidth={4} />
      <hr className="absolute bottom-0 left-[45px] translate-y-[0.5px] border-none h-px w-full bg-white/08 group-last:hidden" />
    </div>
  );
};

export { CollectionTaskCard };
